/**
 * Mobile Callback page.
 *
 * This page is the target of mobile auth redirects. The WebView in the
 * Flutter app intercepts this URL and extracts the token parameter.
 *
 * URL format: /mobile-callback?token=opm_xxx&user=base64_encoded_json
 *
 * If the token is missing, an error is displayed.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

export default function MobileCallback() {
  const location = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      // Token present - the WebView should intercept this page
      // If we reach here in a browser, show success message
      setStatus('success');
    } else {
      setStatus('error');
      setErrorMessage('No token received. Please try logging in again.');
    }
  }, [location.search]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Processing login..." />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-content-primary mb-2">Login Failed</h1>
          <p className="text-content-secondary mb-4">{errorMessage}</p>
          <a
            href="/login"
            className="btn-primary inline-block"
          >
            Try Again
          </a>
        </div>
      </div>
    );
  }

  // Success state - user might see this if WebView doesn't intercept
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full text-center">
        <div className="text-green-500 mb-4">
          <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-content-primary mb-2">Login Successful</h1>
        <p className="text-content-secondary">
          You can close this window and return to the app.
        </p>
      </div>
    </div>
  );
}
